// Лента в стиле Word: вкладки -> группы -> элементы (всё из реестров registry.ribbon*).
// «Файл» открывает бэкстейдж. Узкое окно - группы справа сворачиваются в кнопку-меню (как в Word);
// лента сворачивается до вкладок (Ctrl+F1 или двойной щелчок по вкладке) - тогда вкладка открывается поверх листа.
// Клавиатура: стрелки по вкладкам, ↓/Enter - в кнопки вкладки, Esc - обратно в документ.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { registry, type RibbonItem, type MenuItemSpec, type RibbonGroup } from '../core/registry';
import { ctx, ctxFor, runCommand } from '../core/ctx';
import { useEditorTick, useRegistry } from './hooks';
import { Btn, MenuList, Popover } from './controls';
import { Icon } from './icons';
import { appStore, readPref, writePref } from '../core/store';
import { uiStore, useUi } from './uiState';

const COLLAPSED_W = 96;
/** Порядок свёртывания групп в узком окне: сначала второстепенные, стили и вставки - в последнюю очередь. */
const FOLD_FIRST = ['home.clip', 'home.edit', 'view.look', 'view.show', 'view.mode', 'help.main'];
const FOLD_LAST = ['home.styles', 'home.font', 'insert.objects', 'formulas.insert', 'sto.check', 'refs.sources'];
function foldOrder(ids: string[]): string[] {
  const first = FOLD_FIRST.filter((x) => ids.includes(x));
  const last = [...ids].reverse().filter((x) => FOLD_LAST.includes(x));
  const mid = [...ids].reverse().filter((x) => !first.includes(x) && !last.includes(x));
  return [...first, ...mid, ...last];
}

export function Ribbon() {
  const tabs = useRegistry(registry.ribbonTabs).filter((t) => !t.contextual || safe(() => t.contextual!(ctx)));
  const groups = useRegistry(registry.ribbonGroups);
  const items = useRegistry(registry.ribbonItems);
  useEditorTick();
  const collapsedRibbon = useUi((s) => s.ribbonCollapsed);
  const focusMode = useUi((s) => s.focusMode);
  const [active, setActive] = useState<string>(() => readPref('ribbon.tab', 'home'));
  const [peek, setPeek] = useState(false);
  const shown = tabs.filter((t) => t.id !== 'file');
  const cur = shown.find((t) => t.id === active) || shown.find((t) => t.id === 'home') || shown[0];
  const tabGroups = groups.filter((g) => g.tab === cur?.id && items.some((i) => i.group === g.id));
  const pick = (id: string) => {
    if (id === 'file') { uiStore.getState().set({ backstage: 'info' }); return; }
    setActive(id); writePref('ribbon.tab', id);
    if (uiStore.getState().ribbonCollapsed) setPeek(true);
  };
  // контекстная вкладка (например, «Таблица») - открыть при появлении
  const ctxTab = tabs.find((t) => t.contextual)?.id;
  const prevCtx = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (ctxTab && ctxTab !== prevCtx.current) setActive(ctxTab);
    if (!ctxTab && prevCtx.current && active === prevCtx.current) setActive(readPref('ribbon.tab', 'home'));
    prevCtx.current = ctxTab;
  }, [ctxTab]);

  // --- адаптивное свёртывание групп
  const bodyRef = useRef<HTMLDivElement>(null);
  const widths = useRef(new Map<string, number>());
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const [ver, setVer] = useState(0);
  const sig = cur?.id + ':' + tabGroups.map((g) => g.id + '/' + items.filter((i) => i.group === g.id).length).join(',');
  useLayoutEffect(() => { setFolded(new Set()); }, [sig]);
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    body.querySelectorAll<HTMLElement>(':scope > .ribbon-group:not(.is-folded)').forEach((el) => {
      widths.current.set(el.dataset.group!, el.offsetWidth);
    });
    const avail = body.clientWidth - 40;
    const need = (set: Set<string>) => tabGroups.reduce((s, g) => s + (set.has(g.id) ? COLLAPSED_W : widths.current.get(g.id) || 120), 0);
    const next = new Set<string>();
    for (const id of foldOrder(tabGroups.map((g) => g.id))) { if (need(next) <= avail) break; next.add(id); }
    if (next.size !== folded.size || [...next].some((x) => !folded.has(x))) setFolded(next);
  });
  const bodyVisibleNow = !collapsedRibbon || peek;
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const ro = new ResizeObserver(() => setVer((v) => v + 1));
    ro.observe(body);
    return () => ro.disconnect();
  }, [bodyVisibleNow, focusMode]);
  void ver;

  // --- «подсмотр» свернутой ленты
  useEffect(() => {
    if (!peek) return;
    const down = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest?.('.ribbon, .popover')) setPeek(false); };
    document.addEventListener('mousedown', down, true);
    return () => document.removeEventListener('mousedown', down, true);
  }, [peek]);

  const onTabKey = (e: React.KeyboardEvent, i: number) => {
    const all = [tabs.find((t) => t.id === 'file'), ...shown].filter(Boolean) as typeof tabs;
    const at = all.findIndex((t) => t.id === (i < 0 ? 'file' : shown[i].id));
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const n = all[(at + (e.key === 'ArrowRight' ? 1 : -1) + all.length) % all.length];
      const el = document.querySelector(`[data-testid="tab-${n.id}"]`) as HTMLElement | null;
      el?.focus();
      if (n.id !== 'file') pick(n.id);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      (bodyRef.current?.querySelector('button:not(:disabled)') as HTMLElement | null)?.focus();
    } else if (e.key === 'Escape') {
      appStore.getState().editor?.commands.focus();
    }
  };

  if (focusMode) return null;
  const bodyVisible = !collapsedRibbon || peek;
  return (
    <div className={'ribbon' + (collapsedRibbon ? ' is-collapsed' : '') + (peek ? ' is-peek' : '')}>
      <div className="ribbon-tabs" role="tablist" aria-label="Лента">
        {tabs.map((t) => {
          const i = shown.indexOf(t);
          const isCur = t.id === cur?.id && t.id !== 'file';
          return (
            <button key={t.id} type="button" role="tab" data-testid={'tab-' + t.id} aria-selected={isCur} tabIndex={isCur ? 0 : -1}
              className={'ribbon-tab' + (isCur && bodyVisible ? ' is-active' : '') + (isCur ? ' is-current' : '') + (t.id === 'file' ? ' ribbon-tab-file' : '') + (t.contextual ? ' ribbon-tab-ctx' : '')}
              onMouseDown={(e) => e.preventDefault()} onClick={() => pick(t.id)}
              onDoubleClick={() => { if (t.id !== 'file') { uiStore.getState().set({ ribbonCollapsed: !collapsedRibbon }); setPeek(false); } }}
              onKeyDown={(e) => onTabKey(e, i)}>{t.title}</button>
          );
        })}
        <span className="topbar-spacer" />
        <button type="button" className="ribbon-pin" onClick={() => { uiStore.getState().set({ ribbonCollapsed: !collapsedRibbon }); setPeek(false); }}
          data-tip={collapsedRibbon ? 'Закрепить ленту' : 'Свернуть ленту'} data-tip-key="Ctrl+F1" aria-label={collapsedRibbon ? 'Закрепить ленту' : 'Свернуть ленту'}
          data-testid="ribbon-pin">
          <Icon name={collapsedRibbon ? 'ribbon-expand' : 'ribbon-collapse'} size={15} />
        </button>
      </div>
      {bodyVisible && (
        <div className="ribbon-body" ref={bodyRef} role="tabpanel" aria-label={cur?.title} data-testid="ribbon-body"
          onClick={(e) => { if (peek && (e.target as HTMLElement).closest('button') && !(e.target as HTMLElement).closest('.ribbon-menu, .ribbon-folded')) setPeek(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { appStore.getState().editor?.commands.focus(); setPeek(false); }
            if (e.key === 'ArrowUp' && !(e.target as HTMLElement).closest('.popover')) (document.querySelector('.ribbon-tab.is-current') as HTMLElement | null)?.focus();
          }}>
          {tabGroups.map((g) => (folded.has(g.id)
            ? <FoldedGroup key={g.id} g={g} items={items.filter((i) => i.group === g.id)} />
            : (
              <div key={g.id} className="ribbon-group" data-group={g.id} role="group" aria-label={g.title}>
                <div className="ribbon-group-items">{items.filter((i) => i.group === g.id).map((it) => <RibbonEntry key={it.id} item={it} />)}</div>
                <div className="ribbon-group-title">{g.title}</div>
              </div>
            )))}
        </div>
      )}
    </div>
  );
}

function safe(f: () => boolean) { try { return f(); } catch { return false; } }

/** Свёрнутая группа: одна кнопка, по щелчку - содержимое группы во всплывающем окне. */
function FoldedGroup({ g, items }: { g: RibbonGroup; items: RibbonItem[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const first = items[0];
  const icon = first?.icon || (first?.command ? registry.commands.get(first.command)?.icon : undefined) || 'more';
  return (
    <div ref={ref} className="ribbon-group is-folded" data-group={g.id}>
      <Btn icon={icon} label={g.title} large className="ribbon-folded" onClick={() => setOpen(!open)} testid={'rbg-' + g.id}>
        <Icon name="down" size={12} />
      </Btn>
      {open && (
        <Popover anchor={ref.current} onClose={() => setOpen(false)}>
          <div className="ribbon-group-pop" onClick={(e) => { if ((e.target as HTMLElement).closest('button') && !(e.target as HTMLElement).closest('.ribbon-menu')) setOpen(false); }}>
            <div className="ribbon-group-items">{items.map((it) => <RibbonEntry key={it.id} item={it} />)}</div>
            <div className="ribbon-group-title">{g.title}</div>
          </div>
        </Popover>
      )}
    </div>
  );
}

function resolveItems(it: RibbonItem): MenuItemSpec[] {
  return typeof it.items === 'function' ? it.items(ctx) : it.items || [];
}

export function RibbonEntry({ item }: { item: RibbonItem }) {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const cmd = item.command ? registry.commands.get(item.command) : undefined;
  const label = item.label ?? cmd?.title;
  const icon = item.icon ?? cmd?.icon;
  const title = cmd?.title || label || '';
  const desc = item.hint || cmd?.description;
  const cc = cmd ? ctxFor(cmd.id) : ctx;
  const enabled = cmd?.isEnabled ? safe(() => cmd.isEnabled!(cc)) : true;
  const active = cmd?.isActive ? safe(() => cmd.isActive!(cc)) : false;
  const testid = 'rb-' + item.id;

  if (item.type === 'custom') return <div className="ribbon-custom" data-testid={testid}>{item.render?.(ctx)}</div>;
  if (item.type === 'menu' || item.type === 'split' || item.type === 'gallery') {
    const isSplit = item.type === 'split' && !!cmd;
    return (
      <div ref={ref} className={'ribbon-menu ribbon-' + item.type}>
        {isSplit && (
          <Btn icon={icon} label={label} title={title} desc={desc} shortcut={cmd?.shortcut} large disabled={!enabled} active={active}
            onClick={() => runCommand(item.command!, item.arg)} testid={testid} />
        )}
        <Btn icon={isSplit ? undefined : icon} label={isSplit ? undefined : label} title={isSplit ? 'Ещё' : title} desc={isSplit ? undefined : desc}
          large={!isSplit} className="ribbon-drop" onClick={() => setOpen(!open)} testid={isSplit ? testid + '-menu' : testid}>
          <Icon name="down" size={12} />
        </Btn>
        {open && (
          <Popover anchor={ref.current} onClose={() => setOpen(false)}>
            <MenuList items={resolveItems(item)} onDone={() => setOpen(false)} autoFocus />
          </Popover>
        )}
      </div>
    );
  }
  return (
    <Btn icon={icon} label={item.type === 'large' || item.label ? label : undefined} title={title} desc={desc} shortcut={cmd?.shortcut}
      large={item.type === 'large'} disabled={!enabled} active={item.type === 'toggle' ? active : undefined} testid={testid}
      onClick={() => item.command && runCommand(item.command, item.arg)} />
  );
}
